import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DataType } from '@core/constants/data-types.constants';

@Component({
  selector: 'mfp-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrls: ['./radio-button.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioButtonComponent),
      multi: true
    }
  ]
})
export class RadioButtonComponent implements OnInit, ControlValueAccessor {
  @Input() view = 'agent';
  @Input() idRadio = '';
  @Input() name = '';
  @Input() valueRadio: DataType = '';
  @Input() disabled = false;
  @Input() label = '';
  @Input() labelSize: 'medium' | 'large' = 'medium';

  // FIXME: This 'relatedFormControl' hack should be fixed with something similar
  // to how Angular Material and Ionic solve the activation of the radio-buttons
  // that are related on the same group.
  /**
   * This input is used to relate the RadioButtonComponents that belong to the
   * same 'radio-button-group' by using their related FormControl.
   */
  @Input() relatedFormControl?: any;
  /**
   * This property is used to check if a call to the 'relatedFormControl' should
   * be done or not because it was already done.
   */
  private relatedFormCallAvailable = false;

  private innerValue: DataType = '';

  constructor() {}

  // ControlValueAccessor
  private onChangeCallback = (_: DataType) => {};
  private onTouchCallback = () => {};

  get value(): DataType {
    return this.innerValue;
  }

  set value(newValue: DataType) {
    if (this.innerValue !== newValue) {
      this.innerValue = newValue;
    }
    if (newValue === this.valueRadio) {
      /**
       * If the new value is equal to the one the radio-button is intended for,
       * then we should perform the activation of all the other related radio-buttons
       * in order to modify their state.
       * Since we call all the other related radio-buttons and we are one of them,
       * we should call this once and check that we do not call again recursively.
       */
      if (this.relatedFormCallAvailable) {
        this.relatedFormCallAvailable = false;
        if (this.relatedFormControl) {
          /**
           * If we have a related form control specified, then we should call a
           * value change on it and this will do also the same as 'onChange' and
           * 'onTouch' callbacks functionality: communicate the form that the
           * value has changed.
           */
          this.relatedFormControl.setValue(newValue);
        } else {
          /**
           * If not, then we should perform the 'onChange' and 'onTouch' calls
           * to communicate the form that our value has changed.
           */
          this.onChangeCallback(this.innerValue);
          this.onTouchCallback();
        }
      }
    } else {
      /**
       * If the new value isn't the same as our intended one, then it means that
       * another radio-button has been set so we can enable again the related
       * form call for the next time.
       */
      this.relatedFormCallAvailable = true;
    }
  }

  ngOnInit(): void {
    this.innerValue = this.valueRadio;
  }

  writeValue(value: DataType): void {
    this.value = value;
  }

  registerOnChange(fn: (_: DataType) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchCallback = fn;
  }

  setDisabledState(state: boolean): void {
    this.disabled = state;
  }
}
